import React, { useEffect, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  CircularProgress,
  Tooltip,
  IconButton,
  capitalize,
} from "@mui/material";
import { Trans } from "@lingui/react/macro";
import LoanValidationField from "@/components/review/LoanValidationField";
import {
  Amount,
  Loan,
  LoanKey,
  RetrievedLoanInformation,
  useDealContext,
} from "@/contexts/DealContext";
import { getLoanKeysToDisplay, getLoanStatus } from "@/utils/loanStatusUtils";
import { useNavigate } from "react-router";
import { getMimeExtension } from "../ui/mimeTypes";
import DynamicIcon from "../ui/DynamicIcon";

type LoanValidationStepProps = {
  loan: Loan; // Peut être null si les données ne sont pas encore chargées
  keyName: LoanKey;
};

const LoanValidationStep: React.FC<LoanValidationStepProps> = ({ loan, keyName }) => {
  const { setSubmitFunction, updateLoan, setIsStepValid, setDrawerOpen, drawerOpen } =
    useDealContext();
  const [validatedLoan, setValidatedLoan] = useState<Loan | null>(loan);
  const { status } = getLoanStatus(loan[keyName]);
  const [loading, setLoading] = useState(status === "Searching");
  const navigate = useNavigate();
  const doUpdateLoan = () => {
    if (validatedLoan) {
      const isRetrievedLoanInformation =
        typeof validatedLoan[keyName] === "object" && "value" in (validatedLoan as any)[keyName];
      const data: Record<string, any> = isRetrievedLoanInformation
        ? { ...validatedLoan[keyName], validated: true }
        : {};
      if (!isRetrievedLoanInformation) {
        validatedLoan[keyName] &&
          Object.keys(validatedLoan[keyName] as any).forEach((key) => {
            data[key] = {
              ...(validatedLoan[keyName] as Record<string, any>)[key],
              validated: true,
            };
          });
      }
      console.log("submitFunction", keyName, data);
      updateLoan(loan.id, {
        [keyName]: data,
      });
    }
  }
  const submitFunction = () => {
    doUpdateLoan();
    const keyIndex = getLoanKeysToDisplay(loan).indexOf(keyName);
    const nextKey =
      keyIndex + 1 < getLoanKeysToDisplay(loan).length
        ? getLoanKeysToDisplay(loan)[keyIndex + 1]
        : null;
    const invalidatedKeys = getLoanKeysToDisplay(loan).filter((key) => {
      const { status } = getLoanStatus(validatedLoan?.[key]);
      return status !== "Validated" && key !== keyName;
    });
    if (nextKey === null && invalidatedKeys.length > 0) {
      navigate(`/review/loan/${loan.id}/${invalidatedKeys[0]}`);
    } else if (nextKey && invalidatedKeys.length > 0) {
      navigate(`/review/loan/${loan.id}/${nextKey}`);
    } else {
      navigate(`/loan/${loan.id}`);
    }
  };

  useEffect(() => {
    if (loan) {
      setValidatedLoan(loan);
      setLoading(status === "Searching");
    }
    setSubmitFunction(() => submitFunction);
  }, [loan, status, keyName]);

  const handleFieldChange = (key: keyof Loan, value: RetrievedLoanInformation | Amount) => {
    setIsStepValid(true);
    console.log("handleFieldChange", key, value);
    setValidatedLoan((prev) => ({
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      ...prev!,
      [key]: value,
    }));
    if (key !== "contacts") {
      updateLoan(loan.id, {
        [key]: { ...value, validated: true },
      });
    }
  };

  useEffect(() => {
    setSubmitFunction(() => submitFunction);
  }, [validatedLoan]);

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        flexDirection="column"
        height="100%"
      >
        <CircularProgress />
        <Typography variant="subtitle1">
          <Trans>Our IA model is searching for the relevant data</Trans>
        </Typography>
      </Box>
    );
  }

  const formatedMime = (mimeType: string) =>
    getMimeExtension(mimeType) === "?" ? mimeType : getMimeExtension(mimeType);

  return (
    <Box display="flex" flexDirection="column">
      <Paper elevation={0} sx={{ flex: 1, overflow: "auto", p: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="subtitle1" gutterBottom>
            <Trans>
              Check and confirm <b>{capitalize(keyName.replaceAll("_", " "))}</b>
            </Trans>
          </Typography>
          <Tooltip title={`Preview of ${loan.file?.name}`}>
            <IconButton onClick={() => setDrawerOpen(!drawerOpen)}>
              <DynamicIcon
                iconName={`File${formatedMime(loan.file?.type as string)}`}
                fallback="FileUnknown"
              />
            </IconButton>
          </Tooltip>
        </Box>
        <LoanValidationField
          keyName={keyName}
          data={validatedLoan?.[keyName] || {}}
          onChange={(value: RetrievedLoanInformation | Amount) => handleFieldChange(keyName, value)}
        />
      </Paper>
    </Box>
  );
};

export default LoanValidationStep;
